import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { branch, compose, lifecycle, renderComponent, withState } from 'recompose';
import moment from 'moment-timezone';
import {
  clone,
  get,
  set,
  last,
  toString,
  isNil,
  debounce,
  partial,
} from 'lodash';
import once from 'ramda/src/once';

import { EventEmitter } from 'events';

import withWindowHeight from '../../../common/hoc/withWindowHeight';
import {
  updateAppointment,
  addAppointment,
  scrollToAppointment,
  loadOwnerCustomerNotes,
  resetCustomerNotes,
  setPromoCode,
  saveOwnerCustomer,
} from '../actions';
import * as selector from '../../barbers/selector';
import * as serviceSelector from '../../services/selector';
import { getLoyaltyProgram } from '../../settings/loyalty/selector';
import AppointmentForm from './appointmentForm';
import EditAppointmentForm from './editAppointmentForm';
import { APPOINTMENT_STATUS } from '../constants';
import {
  makeServicesArray,
  countTotalDuration,
  isPaid,
} from '../../../common/helpers/appointments';
import { getStartOfDay } from '../../../common/helpers/times';
import { showNotification } from '../../../common/helpers/notifications';
import config from '../../../../config';
import { fullName } from '../../../common/helpers/users';
import {
  getCustomerNotes,
  getPromoAmount,
  getPromoCode,
} from '../appointmentSelector';
import { activeShop, getIsShopUserOrRentalBarberUser } from '../../../auth/selectors';
import withCurrency from '../../../common/hoc/withCurrency';
import confirmModal from '../../../common/confirmModal';
import { CONFIRMATION_REQUIRED_STATUS } from '../../constants';
import { updateOwnerCustomer } from '../../customers/actions';
import { getBarbers } from '../../barbers/actions';
import withActiveUserIdAndType from '../../../common/hoc/withActiveUserIdAndType';
import withPermissions from '../../../auth/utils/withPermissions';
import { ChurnZeroService, BOOK_APPOINTMENT, PLATFORM } from '../../../../modules/infra/observability/churnzero';
import Spinner from '../../../common/Spinner';
import { withServicePricesFromAppointment } from './utils';
import { APPOINTMENT_CREATION_STARTED, CustomerIoTracker } from '../../../../modules/infra/observability/customer-io';
import { RudderstackTracker, APPOINTMENT_DETAILS_VIEWED } from '../../../../modules/infra/observability/rudderstack';

const AppointmentFormWithPermissions = withPermissions(AppointmentForm);

class AppointmentFormContainer extends Component {
  constructor(props) {
    super(props);

    this.initialState = {
      selectedBarber: null,
      currentBarber: null,
      appointmentId: null,
      day: null,
      time: null,
      dateTime: null,
      customerError: false,
      barberError: false,
      serviceError: false,
      dateError: false,
      timeError: false,
      notesError: false,
      selectedServices: [],
      selectedCustomer: null,
      customDuration: false,
      appointmentNotes: '',
      selectedStatus: APPOINTMENT_STATUS.initial,
      paidInShop: false,
      waiver: false,
      waiverViewLink: '',
      noShow: false,
      allowMultipleServices: false,
      saveButtonDisabled: false,
      confirmationScreenShowed: false,
      sendNotifications: true,
      isRecurring: false,
      recurrEveryNumDays: -1,
      recurrUntil: null,
      isPaid: false,
      areChangesPending: false,
      doNotChargeTax: false,
      autoChargeRecurring: false,
      tipAmount: 0,
      bookedWithAnyBarber: false,
      selectedLeadId: '',
      barberReady: false,
    };

    this.state = this.initialState;

    this.newCustomerDetails = {};
    this.props.setPromoCode({ promoAmount: 0, promoCode: '' });
  }

  componentDidMount() {
    EventEmitter.prototype.on('modal-closed', this.resetForm);

    const isCreatingFromAnotherAppointment = this.props.populateFromAppointment && !this.props.edit;
    if (isCreatingFromAnotherAppointment) {
      this.prePopulateValuesFromAppointment();
    } else {
      this.setAppointmentValues();
    }

    // the savedData prop is used to sync the form data and UI with the data that come back from the API
    // once an appointment is created or updated. DO NOT extend or modify this code or use the savedData prop
    // UNLESS the changes you need to make HAVE TO be done at that specific moment
    this.populateFormWithSavedData(this.props.savedData);

    this.props.router.setRouteLeaveHook( // eslint-disable-line react/prop-types
      this.getActiveRoute(),
      this.routerWillLeave
    );
  }

  componentWillUnmount() {
    EventEmitter.prototype.removeListener('modal-closed', this.resetForm);
    this.props.resetCustomerNotes();
    this.removeRouteLeaveHook();
  }

  onChangeAppointmentNotes = (notes) => {
    this.setState({
      appointmentNotes: notes,
    });
  }

  onRecurringFrequencyChange = (e) => {
    this.setState({
      recurrEveryNumDays: parseInt(e.target.value, 10),
      isRecurring: parseInt(e.target.value, 10) > 0,
    });
  }

  onRecurringEndDateChange = (date) => {
    this.setState({ recurrUntil: date });
  }

  onAnyBarberChange = (bookedWithAnyBarber) => {
    this.setState({ bookedWithAnyBarber });
  }

  getActiveRoute() {
    return last(this.props.routes); // eslint-disable-line react/prop-types
  }

  setDate = ({ day, time }) => {
    const momentTime = moment(time, 'HH:mm');
    const dateTime = (() => {
      if (!day || !time) return null;
      return getStartOfDay(this.props.timezone, day)
        .hours(momentTime.hours())
        .minutes(momentTime.minutes())
        .toJSON();
    })();

    this.setState({
      day,
      time,
      dateTime,
      areChangesPending: true,
    });
  }

  getBarberSelectedServices(barber) {
    const { selectedServices } = this.state;
    const { getBarberServices } = this.props;
    const barberServices = getBarberServices(barber.id);
    const services = selectedServices.reduce((acc, selectedService) => {
      const id = selectedService.serviceId || selectedService.id;
      const serviceOfBarberId = barberServices.find(service => service.id === id);

      if (serviceOfBarberId) {
        acc.push(serviceOfBarberId);
      }

      return acc;
    }, []);

    if (!barber.allowMultipleServices) {
      return services.slice(0, 1);
    }

    return services;
  }

  setCustomerDetails = (value, field) => {
    this.newCustomerDetails[field] = value;
  }

  prePopulateValuesFromAppointment = () => {
    const { populateFromAppointment: appointment } = this.props;
    const selectedServices = appointment.appointment_service.map(service => ({
      ...service,
      ...{ id: service.serviceId, costWithTaxes: service.cost },
    }));

    this.selectCustomer({
      ...appointment.customer,
      name: fullName(appointment.customer),
    });

    const currentBarber = this.props.barbers.find(item => item.id === appointment.barberId);

    this.setState({
      currentBarber,
      selectedServices,
      selectedBarber: appointment.barberId,
      selectedDuration: appointment.duration,
      customDuration: appointment.customDuration,
      doNotChargeTax: appointment.doNotChargeTax,
      bookedWithAnyBarber: appointment.bookedWithAnyBarber,
      allowMultipleServices: currentBarber.allowMultipleServices,
    });
  };

  setAppointmentValues = () => {
    const { appointment, timezone } = this.props;
    if (appointment) {
      let day = null;
      let time = null;
      let selectedServices = [];
      if (appointment && appointment.appointment_service) {
        selectedServices = appointment.appointment_service.map(service => ({
          ...service,
          ...{ id: service.serviceId, costWithTaxes: service.cost },
        }));
      }

      day = moment(appointment.start).tz(timezone).format(config.dateFormatISO);
      time = moment(appointment.start).tz(timezone).format('HH:mm');
      this.setBarber(appointment.barberId);
      set(appointment, 'customer.name', fullName(appointment.customer));
      this.setCustomer(appointment.customer);
      this.setState({
        appointmentId: appointment.id,
        appointmentNotes: appointment.customerNotes,
        selectedBarber: appointment.barberId,
        selectedServices,
        selectedDuration: appointment.duration,
        customDuration: appointment.customDuration,
        selectedStatus: appointment.status,
        day,
        time,
        dateTime: appointment.start,
        barberError: false,
        serviceError: false,
        dateError: false,
        timeError: false,
        paidInShop: appointment.paidInShop,
        waiver: appointment.waiver,
        waiverViewLink: appointment.waiverViewLink,
        noShow: appointment.noShow,
        isPaid: isPaid(appointment),
        doNotChargeTax: appointment.doNotChargeTax,
        isRecurring: appointment.isRecurring,
        recurrEveryNumDays: appointment.recurrEveryNumDays || -1,
        recurrUntil: appointment.recurrUntil,
        autoChargeRecurring: appointment.autoChargeRecurring || false,
        tipAmount: appointment.tipAmount || 0,
        bookedWithAnyBarber: appointment.bookedWithAnyBarber,
        // Override any previous changes that might be triggered before the
        // appointment was loaded
        areChangesPending: false,
        barberReady: appointment.barberReady,
      });

      this.props.setPromoCode({
        promoAmount: appointment.promoAmount,
        promoCode: appointment.promoCode,
      });
    }
  }

  getSelectedBarberData(selected) {
    const barber = (parseInt(selected, 10) === -1 ? null : selected);
    let selectedServices = [];
    let allowMultipleServices = false;
    let selectedDuration;

    const currentBarber = this.props.barbers.filter(item => item.id === barber)[0];
    if (currentBarber) {
      allowMultipleServices = currentBarber.allowMultipleServices;
      selectedServices = this.getBarberSelectedServices(currentBarber);
      if (selectedServices.length === 0) {
        // select default service
        const service = this.props.allServices.find(item => item.id === currentBarber.defaultServiceId);
        if (service) {
          selectedServices.push(service);
        }
      }
      selectedDuration = countTotalDuration(selectedServices) || this.state.selectedDuration;
      this.selectServices(selectedServices);
    }
    return {
      // TODO: Refactor to avoid separate property for barber id, we can get id from currentBarber
      selectedBarber: barber,
      currentBarber,
      selectedServices,
      selectedDuration,
      selectedStatus: APPOINTMENT_STATUS.initial,
      noShow: false,
      allowMultipleServices,
    };
  }

  setBarber = (selected) => {
    const selectedBarberData = this.getSelectedBarberData(selected);
    this.setState({
      ...selectedBarberData,
    });
  }

  setChangesPending = (value = true) => {
    this.setState({ areChangesPending: value });
  }

  getServicesDuration(selectedServices) {
    const services = selectedServices || this.state.selectedServices;
    return countTotalDuration(services);
  }

  setCustomer = (customer) => {
    if (!customer) {
      this.selectLeadId('');
      this.setState({ selectedCustomer: null });
      return;
    }

    if (!this.state.selectedCustomer || customer.id !== this.state.selectedCustomer.id) {
      if (customer.leadId) this.selectLeadId(customer.leadId);
      this.setState({ selectedCustomer: customer });

      if (!customer.deleted) {
        this.props.fetchCustomer(customer.id).then((response) => {
          if (!customer.leadId) this.selectLeadId(response.leadId);

          // extend typesense customer data with data from api
          this.setState({ selectedCustomer: { ...customer, ...response } });
        });
      }
    }
  };

  setTipAmount = (value) => {
    this.setState({
      tipAmount: value,
      areChangesPending: true,
    });
  }

  getModalType() {
    if (this.props.edit) return 'details';
    return 'new';
  }

  populateFormWithSavedData(data) {
    const savedData = { ...data };
    const { customer, barberId, day, time, duration } = savedData;

    let selectedBarberData = {};
    let selectedDayData = {};
    let selectedTimeData = {};
    let selectedDurationData = {};

    if (customer && customer !== this.state.selectedCustomer) {
      this.setCustomer(customer);
    }
    if (barberId && barberId !== this.state.selectedBarber) {
      selectedBarberData = this.getSelectedBarberData(barberId);
    }
    if (day && day !== this.state.day) {
      selectedDayData = { day };
    }
    if (time && time !== this.state.time) {
      selectedTimeData = { time };
    }
    if (duration && duration !== this.state.duration) {
      selectedDurationData = {
        duration,
        customDuration: true,
      };
    }

    if (customer || barberId || day || time || duration) {
      this.setState({
        ...selectedBarberData,
        ...selectedDayData,
        ...selectedTimeData,
        ...selectedDurationData,
      });
    }
  }

  selectBarber = (barber) => {
    this.setBarber(barber);
    this.setState({
      areChangesPending: true,
    });
  }

  selectServices = (selectedServices) => {
    const nextSelectedServices = clone(withServicePricesFromAppointment(selectedServices, this.props.appointment));
    const newState = {
      selectedServices: nextSelectedServices,
      selectedDuration: this.getServicesDuration(selectedServices),
      customDuration: false,
      areChangesPending: true,
    };

    if (this.props.edit) {
      delete newState.selectedDuration;
      newState.customDuration = true;
    }

    this.setState(newState);
  }

  selectDuration = (duration) => {
    this.setState({
      customDuration: true,
      selectedDuration: duration,
    });
  }

  selectCustomer = (customer) => {
    this.setCustomer(customer);
    this.setState({
      areChangesPending: true,
    });
  }

  toggleDoNotChargeTax = (value) => {
    this.setState({
      doNotChargeTax: value,
      areChangesPending: true,
    });
  }

  toggleAutoChargeRecurring = (value) => {
    this.setState({
      autoChargeRecurring: value,
      areChangesPending: true,
    });
  }

  closeModal = () => {
    EventEmitter.prototype.emit('close-appointment-modal', this.getModalType());
  }

  routerWillLeave = () => {
    if (this.state.areChangesPending && !confirm('There are unsaved changes! Are you sure?')) {
      return false;
    }

    this.closeModal();
    return null;
  }

  removeRouteLeaveHook = () => {
    const removeHook = this.props.router.setRouteLeaveHook( // eslint-disable-line react/prop-types
      this.getActiveRoute(),
      () => removeHook()
    );
  }

  toggleArrived = once(() => {
    // just save and close everything
    let status;
    if (this.state.selectedStatus !== APPOINTMENT_STATUS.arrived) {
      status = APPOINTMENT_STATUS.arrived;
    } else {
      status = APPOINTMENT_STATUS.initial;
    }
    this.props.modifyAppointment(this.props.appointment.id, {
      status,
      noShow: this.state.noShow && !status,
      paidInShop: this.state.paidInShop && !!status,
      sendNotifications: true,
    });
    EventEmitter.prototype.emit('appointment-saved');
    EventEmitter.prototype.emit('close-appointment-modal', 'details');
  });

  toggleBarberReady = debounce(() => {
    const barberReady = this.state.barberReady;
    this.setState({
      barberReady: !barberReady,
    });
    this.props.modifyAppointment(this.props.appointment.id, {
      barberReady: !barberReady,
      sendNotifications: true,
    }).then((result) => {
      if (result.barberReady) {
        showNotification({
          title: 'SUCCESS',
          message: 'Notification sent to customer successfully.',
          level: 'success',
        });
      }
    });
    EventEmitter.prototype.emit('appointment-saved');
  }, 600, {
    leading: true,
    trailing: false,
  });

  toggleNoShow = once(() => {
    if (!this.state.noShow) {
      this.props.modifyAppointment(this.props.appointment.id, {
        paidInShop: false,
        status: APPOINTMENT_STATUS.initial,
        noShow: true,
      });
    } else {
      this.props.modifyAppointment(this.props.appointment.id, {
        noShow: false,
      });
    }
    EventEmitter.prototype.emit('appointment-saved');
    EventEmitter.prototype.emit('close-appointment-modal', 'details');

    if (!this.state.noShow && !this.props.activeShop.blockNoShowFromBookNoPay) {
      const { customer } = this.props.appointment;
      confirmModal(
        {
          title: customer.name,
          message: (
            <span className="u-fontsize-normal">
              Would you like to mark this client as pre-pay only for future bookings?
            </span>
          ),
          type: 'yesno',
          confirmButtonText: 'Yes',
          negativeButtonText: 'No',
        },
        () => {
          const data = { blockNoShowFromBookNoPay: true };
          this.props.updateOwnerCustomer(this.props.activeId, this.props.activeType, customer.id, data);
        },
      );
    } else if (!this.state.noShow) {
      const { customer } = this.props.appointment;
      confirmModal(
        {
          title: customer.name,
          message: (
            <span className="u-fontsize-normal">
              Per Shop settings, this client has been marked as Prepay Only.
            </span>
          ),
          showNegativeButton: false,
          confirmButtonText: 'Ok',
        },
      );
    }
  });

  togglePaidInShop = async () => {
    const confirmed = await confirmModal({
      type: 'danger',
      title: 'Paid in shop',
      message: 'Are you sure that this appointment is paid in shop?',
    });

    if (!confirmed) {
      return;
    }

    const update = this.state.paidInShop
      ? { paidInShop: false }
      : {
        status: APPOINTMENT_STATUS.arrived,
        paidInShop: true,
        noShow: false,
      };

    this.disableSubmit();
    this.setState(
      update,
      () => {
        this.props.modifyAppointment(this.props.appointment.id, update)
          .then(() => {
            showNotification({
              title: 'SUCCESS',
              message: 'Appointment successfully saved',
              level: 'success',
            });

            EventEmitter.prototype.emit('appointment-saved');
            EventEmitter.prototype.emit(this.modalEvent, 'details');
          })
        .finally(() => {
          this.enableSubmit();
        });
      },
    );
  };

  isNotValid() {
    const {
      selectedBarber,
      selectedCustomer,
      selectedServices,
      day,
      time,
      appointmentNotes,
    } = this.state;

    const newCustomerError =
      !(this.newCustomerDetails && this.newCustomerDetails.name && this.newCustomerDetails.name.length > 1);
    const customerError = !selectedCustomer && newCustomerError;
    const barberError = !selectedBarber;
    const serviceError = !(selectedServices && selectedServices.length >= 1);
    const dateError = !(day && moment(day, config.dateFormatISO).isValid());
    const timeError = !time;
    const notes = (appointmentNotes && appointmentNotes.trim()) || '';
    const notesError = !!notes && notes.length < 3;

    this.setState({
      customerError,
      barberError,
      serviceError,
      dateError,
      timeError,
      notesError,
      appointmentNotes: notes,
    });
    return !!(barberError || serviceError || timeError || dateError || notesError);
  }

  update = (skipConfirmationScreen = false) => (e) => {
    if (e) e.preventDefault();
    const { confirmationScreenShowed } = this.state;

    if (this.isNotValid()) {
      return false;
    }

    if (this.props.edit && !confirmationScreenShowed && !skipConfirmationScreen) {
      this.showConfirmationScreen();
    } else {
      return this.save();
    }
    return false;
  }

  saveAll = (e) => {
    this.save(e, true);
  }

  save = (e = false, saveAll, notify) => {
    if (e) e.preventDefault();

    const saveAllBool = typeof saveAll === 'boolean' && saveAll;

    const {
      timezone,
      promoCode,
      appointment,
    } = this.props;
    const {
      selectedCustomer,
      appointmentNotes,
      selectedBarber,
      selectedServices,
      selectedDuration,
      selectedStatus,
      customDuration,
      day,
      time,
      paidInShop,
      waiver,
      waiverViewLink,
      noShow,
      sendNotifications,
      isRecurring,
      recurrEveryNumDays,
      recurrUntil,
      doNotChargeTax,
      autoChargeRecurring,
      tipAmount,
      bookedWithAnyBarber,
    } = this.state;

    if (this.isNotValid()) {
      return null;
    }

    const model = {
      customerId: selectedCustomer && selectedCustomer.id,
      customerNotes: appointmentNotes,
      barberId: selectedBarber,
      duration: selectedDuration,
      customDuration,
      paidInShop,
      waiver,
      waiverViewLink,
      noShow,
      sendNotifications: isNil(notify) ? sendNotifications : notify,
      isRecurring,
      recurrEveryNumDays,
      recurrUntil,
      rescheduleAllRecurring: saveAllBool,
      doNotChargeTax,
      autoChargeRecurring,
      tipAmount,
      bookedWithAnyBarber,
    };

    if (promoCode) {
      model.promoCode = promoCode;
    }

    if (selectedStatus !== APPOINTMENT_STATUS.canceled) {
      if (!noShow && selectedStatus === APPOINTMENT_STATUS.arrived) {
        model.sendNotifications = true;
      }
      model.status = selectedStatus;
    }

    model.appointment_service = makeServicesArray(selectedServices, appointment);
    const momentTime = moment(time, 'HH:mm');
    model.dateTime = getStartOfDay(timezone, day).hours(momentTime.hours()).minutes(momentTime.minutes()).toJSON();

    // Don't send tip if it isn't changed
    if (appointment && model.tipAmount === appointment.tipAmount) {
      delete model.tipAmount;
    }

    return this.saveAppointment(model);
  }

  prepareNewCustomer() {
    const customerFullName = this.newCustomerDetails.name || '';
    let firstName = '';
    let lastName = '';
    if (customerFullName.indexOf(' ') === -1) {
      firstName = customerFullName;
    } else {
      firstName = customerFullName.substr(0, customerFullName.indexOf(' '));
      lastName = customerFullName.substr(customerFullName.indexOf(' ') + 1);
    }
    let newCustomer = {
      firstName,
    };
    if (lastName) {
      newCustomer = {
        lastName,
        ...newCustomer,
      };
    }
    if (this.newCustomerDetails.phone) {
      newCustomer = {
        phone: this.newCustomerDetails.phone,
        ...newCustomer,
      };
    }
    const email = this.newCustomerDetails.email;
    if (email) {
      newCustomer = {
        email,
        ...newCustomer,
      };
    }

    if (this.props.customerNotes) {
      newCustomer.notes = this.props.customerNotes;
    }

    newCustomer.leadId = this.state.selectedLeadId || null;

    return newCustomer;
  }

  async saveAppointment(data) {
    const model = {
      ...data,
      confirmations: {
        saveOutOfSchedule: 'ask',
        removeInvalidPromo: 'ask',
        saveDoubleBooking: 'ask',
      },
    };

    if (this.isNotValid()) {
      this.enableSubmit();
      return false;
    }

    this.disableSubmit();

    const {
      shop,
      edit,
      saveAppointment,
      modifyAppointment,
      appointment,
      timezone,
      customerNotes,
    } = this.props;


    const updateOrSave = edit ? modifyAppointment.bind(null, appointment.id) : saveAppointment.bind(null, shop);

    const confirmations = {
      OutOfScheduleError: 'saveOutOfSchedule',
      RemoveInvalidPromoError: 'removeInvalidPromo',
      DoubleBookingError: 'saveDoubleBooking',
    };

    const onError = (err) => {
      if (err.status === CONFIRMATION_REQUIRED_STATUS) {
        confirmModal(
          {
            title: 'Heads up!',
            message: err.message,
            type: 'yesno',
            confirmButtonText: 'Yes',
            negativeButtonText: 'No',
          },
          () => {
            set(model, `confirmations.${confirmations[err.code]}`, 'yes');
            updateOrSave(model).then(onResponse).catch(onError);
          },
          () => this.enableSubmit()
        );
      } else {
        this.enableSubmit();
      }
    };

    const onResponse = (response) => {
      const invalidRecurringTimes = get(response, 'errors.invalidRecurringTimes', []);
      const outOfScheduleTimes = get(response, 'errors.outOfScheduleTimes', []);
      const mapTimes = dateTime => moment.tz(dateTime, timezone).format(config.dateTimeFormat);
      const messages = [];

      if (invalidRecurringTimes.length) {
        const invalidTimes = invalidRecurringTimes.map(mapTimes);
        messages.push(
          `There was a conflict on ${invalidTimes.join(', ')}. Please resolve each conflict individually.`
        );
      }

      if (outOfScheduleTimes.length) {
        const times = outOfScheduleTimes.map(mapTimes);
        const firstFiveTimes = times.slice(0, 5);
        let msg = `Following appointment times are outside of barber's schedule: ${firstFiveTimes.join(', ')}`;

        if (times.length > 5) {
          msg += ` and ${times.slice(5).length} more`;
        }

        msg += '. Please verify that the barber will in fact work one these days/times.';
        messages.push(msg);
      }

      if (messages.length) {
        messages.push('Check email for summary.');
        showNotification({
          title: 'WARNING',
          message: messages.join('\n\n'),
          autoDismiss: 15,
        });
      }

      this.props.scrollToAppointment(model.dateTime, timezone, model.barberId);
      EventEmitter.prototype.emit('appointment-saved');

      if (!edit) {
        ChurnZeroService.track(BOOK_APPOINTMENT, PLATFORM);
      }
      this.closeModal();

      return response;
    };

    if (!edit) {
      model.platform = 'Commander';
    }

    const shouldCreateNewCustomer = !model.customerId;
    if (shouldCreateNewCustomer) {
      model.customer = this.prepareNewCustomer();
    }

    return updateOrSave(model)
      .then((response) => {
        if (!shouldCreateNewCustomer) {
          const leadId = this.state.selectedLeadId !== this.state.selectedCustomer
            ? this.state.selectedLeadId
            : undefined;
          this.props.saveCustomer(model.customerId, toString(customerNotes), leadId);
        }
        return response;
      })
      .then(onResponse).catch(onError);
  }

  resetForm = () => {
    this.setState(this.initialState);
  }

  showConfirmationScreen() {
    this.setState({
      confirmationScreenShowed: true,
    });
  }

  hideConfirmationScreen = (e) => {
    e.preventDefault();
    this.setState({
      confirmationScreenShowed: false,
    });
  }

  saveWithoutConfirmation = () => {
    this.setState({
      confirmationScreenShowed: false,
    }, this.save);
  }

  toggleNotifications = (checked) => {
    this.setState({
      sendNotifications: checked,
    });
  }

  selectLeadId = (leadId) => {
    if (this.state.selectedCustomer && this.state.selectedCustomer.leadId !== leadId) this.setChangesPending();
    this.setState({ selectedLeadId: leadId });
  }

  enableSubmit() {
    this.setState({
      saveButtonDisabled: false,
    });
  }

  disableSubmit() {
    this.setState({
      saveButtonDisabled: true,
    });
  }

  render() {
    const {
      toggleArrived,
      toggleNoShow,
      togglePaidInShop,
      save,
      saveAll,
      update,
      hideConfirmationScreen,
      saveWithoutConfirmation,
      toggleNotifications,
      selectCustomer,
      setCustomerDetails,
      newCustomerDetails,
      onChangeAppointmentNotes,
      selectBarber,
      selectServices,
      selectDuration,
      onRecurringSwitchChange,
      onRecurringEndDateChange,
      onRecurringFrequencyChange,
      setDate,
      toggleDoNotChargeTax,
      toggleAutoChargeRecurring,
      setTipAmount,
      setChangesPending,
      onAnyBarberChange,
      selectLeadId,
      toggleBarberReady,
    } = this;

    const handlers = {
      toggleArrived,
      toggleNoShow,
      togglePaidInShop,
      save,
      saveAll,
      update,
      hideConfirmationScreen,
      saveWithoutConfirmation,
      toggleNotifications,
      selectCustomer,
      setCustomerDetails,
      onChangeAppointmentNotes,
      selectBarber,
      selectServices,
      selectDuration,
      onRecurringSwitchChange,
      onRecurringEndDateChange,
      onRecurringFrequencyChange,
      setDate,
      toggleDoNotChargeTax,
      toggleAutoChargeRecurring,
      setTipAmount,
      setChangesPending,
      onAnyBarberChange,
      selectLeadId,
      toggleBarberReady,
    };

    let isRecurring;
    if (this.props.appointment) {
      isRecurring = this.props.appointment.isRecurring;
    } else {
      isRecurring = this.state.isRecurring;
    }

    const props = {
      ...this.props,
      selectedServices: this.state.selectedServices,
      saveButtonDisabled: this.state.saveButtonDisabled,
      selectedStatus: this.state.selectedStatus,
      selectedBarber: this.state.selectedBarber,
      currentBarber: this.state.currentBarber,
      selectedCustomer: this.state.selectedCustomer,
      selectedDuration: this.state.selectedDuration,
      day: this.state.day,
      time: this.state.time,
      dateTime: this.state.dateTime,
      appointmentNotes: this.state.appointmentNotes,
      paidInShop: this.state.paidInShop,
      allowMultipleServices: this.state.allowMultipleServices,
      waiver: this.state.waiver,
      waiverViewLink: this.state.waiverViewLink,
      noShow: this.state.noShow,
      customerError: this.state.customerError,
      barberError: this.state.barberError,
      serviceError: this.state.serviceError,
      dateError: this.state.dateError,
      timeError: this.state.timeError,
      notesError: this.state.notesError,
      confirmationScreenShowed: this.state.confirmationScreenShowed,
      sendNotifications: this.state.sendNotifications,
      customDuration: this.state.customDuration,
      selectedIsRecurring: this.state.isRecurring,
      isRecurring,
      recurrUntil: this.state.recurrUntil,
      recurrEveryNumDays: this.state.recurrEveryNumDays,
      handlers,
      newCustomerDetails,
      isPaid: this.state.isPaid,
      areChangesPending: this.state.areChangesPending,
      doNotChargeTax: this.state.doNotChargeTax,
      autoChargeRecurring: this.state.autoChargeRecurring,
      isShopUserOrRentalBarberUser: this.props.isShopUserOrRentalBarberUser,
      tipAmount: this.state.tipAmount,
      hasLoyaltyProgram: this.props.hasLoyaltyProgram,
      windowHeight: this.props.windowHeight,
      bookedWithAnyBarber: this.state.bookedWithAnyBarber,
      selectedLeadId: this.state.selectedLeadId,
      barberReady: this.state.barberReady,
      onClose: this.closeModal,
    };

    if (this.props.edit) return <EditAppointmentForm {...props} />;
    return <AppointmentFormWithPermissions {...props} />;
  }
}

AppointmentFormContainer.propTypes = {
  // the savedData prop is used to sync the form data and UI with the data that comes back from the API
  savedData: PropTypes.object,

  // an appointment to be updated in the editing mode
  appointment: PropTypes.object,

  // when rebooking or copying an appointment, this prop is where the basic fields are going to be pre-populated from
  // can only be used in the new appointment mode
  populateFromAppointment: PropTypes.object,
  edit: PropTypes.bool,
  shop: PropTypes.string,
  barbers: PropTypes.arrayOf(PropTypes.object).isRequired,
  timezone: PropTypes.string,
  saveAppointment: PropTypes.func.isRequired,
  modifyAppointment: PropTypes.func.isRequired,
  scrollToAppointment: PropTypes.func.isRequired,
  allServices: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchCustomer: PropTypes.func.isRequired,
  saveCustomer: PropTypes.func.isRequired,
  customerNotes: PropTypes.string.isRequired,
  resetCustomerNotes: PropTypes.func.isRequired,
  promoCode: PropTypes.string.isRequired,
  setPromoCode: PropTypes.func.isRequired,
  isShopUserOrRentalBarberUser: PropTypes.bool.isRequired,
  hasLoyaltyProgram: PropTypes.bool.isRequired,
  windowHeight: PropTypes.number.isRequired,
  getBarberServices: PropTypes.func.isRequired,
  activeShop: PropTypes.object.isRequired,
};

AppointmentFormContainer.defaultProps = {
  savedData: null,
  appointment: null,
  populateFromAppointment: null,
  edit: false,
  shop: null,
  timezone: null,
};

const mapStateToProps = state => ({
  shop: state.auth.shop,
  activeShop: activeShop(state),
  barbers: selector.getAll(state),
  timezone: state.auth.timezone,
  user: state.auth.user,
  barberUser: state.auth.barberUser,
  allServices: serviceSelector.forAppointmentForm(state),
  customerNotes: getCustomerNotes(state),
  promoCode: getPromoCode(state),
  promoAmount: getPromoAmount(state),
  isShopUserOrRentalBarberUser: getIsShopUserOrRentalBarberUser(state),
  hasLoyaltyProgram: get(getLoyaltyProgram(state), 'enabled', false),
  getBarberServices: partial(selector.getBarberServices, state),
});

const mapDispatchToProps = {
  saveAppointment: addAppointment,
  modifyAppointment: updateAppointment,
  scrollToAppointment,
  loadOwnerCustomerNotes,
  saveOwnerCustomer,
  resetCustomerNotes,
  setPromoCode,
  updateOwnerCustomer,
  getBarbers,
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const props = Object.assign({}, ownProps, stateProps, dispatchProps);
  props.saveCustomer = (customerId, notes, leadId) =>
    dispatchProps.saveOwnerCustomer(props.activeId, props.activeType, customerId, notes, leadId);
  props.fetchCustomer = customerId => dispatchProps.loadOwnerCustomerNotes(props.activeId, props.activeType, customerId);

  return props;
};

export default compose(
  withActiveUserIdAndType(),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
  withWindowHeight,
  withCurrency,
  withState(
    'barbersFetched',
    'setBarbersFetched',
      props => !(props.activeType === 'brand' && props.appointment)
  ),
  lifecycle({
    componentDidMount() {
      if (this.props.activeType === 'brand' && this.props.appointment) {
        this.props.getBarbers(this.props.appointment.shopId)
          .then(() => this.props.setBarbersFetched(true));
      }

      if (!this.props.edit) {
        CustomerIoTracker.getInstance().track(APPOINTMENT_CREATION_STARTED);
      } else {
        RudderstackTracker.getInstance().track(APPOINTMENT_DETAILS_VIEWED);
      }
    },
  }),
  branch(
    props => !props.barbersFetched,
    renderComponent(Spinner),
  ),
)(AppointmentFormContainer);
